import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import ChildFriendlyOutlinedIcon from "@mui/icons-material/ChildFriendlyOutlined";
import FaceOutlinedIcon from "@mui/icons-material/FaceOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import {alpha} from "@mui/material/styles";
import type {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {AgeCategoryType} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {useTranslation} from "react-i18next";
import {resolveAgeCategoryAppearance} from "../ageCategoryDefaults";
import {ElementChip} from "./base/ElementChip";

export type AgeCategoryElementModel = Pick<
    AgeCategory,
    "id" | "name" | "type" | "alias" | "color"
>;

export type AgeCategoryElementVariant = "chip" | "inline";

interface AgeCategoryElementProps {
    ageCategory: AgeCategoryElementModel;
    count: number;
    asPrint?: boolean;
    variant?: AgeCategoryElementVariant;
}

export function AgeCategoryElement({
    ageCategory,
    count,
    asPrint = false,
    variant = "chip",
}: AgeCategoryElementProps) {
    const {t} = useTranslation();
    const {alias, color} = resolveAgeCategoryAppearance(ageCategory);
    const translationKey = ageCategory.type ? {
        [AgeCategoryType.ADULT]: count === 1 ? "Adult" : "Adults",
        [AgeCategoryType.TEENAGER]: count === 1 ? "Teenager" : "Teenagers",
        [AgeCategoryType.CHILD]: count === 1 ? "Child" : "Children",
        [AgeCategoryType.INFANT]: count === 1 ? "Infant" : "Infants",
    }[ageCategory.type] : count === 1 ? "Guest" : "Guests";
    const description = ageCategory.name
        ? `${count} ${ageCategory.name}`
        : `${count} ${t(translationKey)}`;

    if (asPrint) {
        return <>{alias ? `${alias} ${count}` : description}</>;
    }

    const iconSx = {fontSize: 15, color: color ?? "text.secondary"};
    const defaultIcon = (() => {
        switch (ageCategory.type) {
            case AgeCategoryType.ADULT:
                return count === 1
                    ? <PersonOutlinedIcon sx={iconSx}/>
                    : <PeopleAltOutlinedIcon sx={iconSx}/>;
            case AgeCategoryType.TEENAGER:
                return <FaceOutlinedIcon sx={iconSx}/>;
            case AgeCategoryType.CHILD:
                return <ChildCareOutlinedIcon sx={iconSx}/>;
            case AgeCategoryType.INFANT:
                return <ChildFriendlyOutlinedIcon sx={iconSx}/>;
            default:
                return count === 1
                    ? <PersonOutlinedIcon sx={iconSx}/>
                    : <PeopleAltOutlinedIcon sx={iconSx}/>;
        }
    })();

    if (variant === "inline") {
        return (
            <Tooltip title={description} arrow>
                <Box
                    component="span"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                        flex: "0 0 auto",
                        cursor: "default",
                    }}
                >
                    {alias ? (
                        <Box
                            component="span"
                            sx={{
                                color: color ?? "text.secondary",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                lineHeight: 1,
                                mt: "2px",
                            }}
                        >
                            {alias}
                        </Box>
                    ) : defaultIcon}
                    <Box
                        component="span"
                        sx={{color: "text.secondary", fontSize: "0.75rem", lineHeight: 1}}
                    >
                        {count}
                    </Box>
                </Box>
            </Tooltip>
        );
    }

    return (
        <ElementChip
            text={`${count}`}
            startIcon={alias ? (
                <Box
                    component="span"
                    sx={{
                        color: color ?? "text.secondary",
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1,
                        mt: "2px",
                    }}
                >
                    {alias}
                </Box>
            ) : defaultIcon}
            borderColor={color ? alpha(color, 0.28) : undefined}
            backgroundColor={color ? alpha(color, 0.06) : undefined}
            tooltipContent={description}
        />
    );
}
